import React, {FunctionComponent, useCallback, useMemo} from "react";
import {QueryBuilderColumn, QueryBuilderOperatorEnum, QueryBuilderReducerActionTypeEnum, QueryBuilderReducerDispatch, QueryBuilderStateQueryRule, ThemeMixin} from "../../../index";
import {Checkbox, FormControlLabel, IconButton, MenuItem, Paper, TextField, Theme, Tooltip, Unstable_Grid2 as Grid} from "@mui/material";
import {DeleteOutlined} from "@mui/icons-material";
import QueryBuilderValueInput from "../value-input";

export interface QueryBuilderRuleProps {
    columns: QueryBuilderColumn[];
    rule: QueryBuilderStateQueryRule;
    ruleIndex: number;
    conditionIndex: number;
    dispatch: QueryBuilderReducerDispatch;
    nestedLevel?: number;
    disabled: boolean;
    last: boolean;
}

const QueryBuilderRule: FunctionComponent<QueryBuilderRuleProps> = ({
                                                                        columns,
                                                                        rule,
                                                                        ruleIndex,
                                                                        conditionIndex,
                                                                        dispatch,
                                                                        nestedLevel = 1,
                                                                        disabled,
                                                                        last,
                                                                    }) => {
    const column = useMemo(() => columns.find(column => column.field === rule.field && column.column === rule.column), [columns, rule.field, rule.column])

    const fieldOptions = useMemo(() => columns.map((column) => ({
        value: {field: column.field, column: column.column},
        label: column.column,
    })), [columns])

    const getOperatorLabel = useCallback((operator: string) => {
        switch (operator) {
            case QueryBuilderOperatorEnum.equal:
                return "Equal";
            case QueryBuilderOperatorEnum.notEqual:
                return "Not Equal";
            case QueryBuilderOperatorEnum.greaterThan:
                return "Greater Than";
            case QueryBuilderOperatorEnum.greaterThanOrEqual:
                return "Greater Than Or Equal";
            case QueryBuilderOperatorEnum.lessThan:
                return "Less Than";
            case QueryBuilderOperatorEnum.lessThanOrEqual:
                return "Less Than Or Equal";
            case QueryBuilderOperatorEnum.contains:
                return "Contains";
            case QueryBuilderOperatorEnum.notContains:
                return "Not Contains";
            case QueryBuilderOperatorEnum.startsWith:
                return "Starts With";
            case QueryBuilderOperatorEnum.endsWith:
                return "Ends With";
            case QueryBuilderOperatorEnum.in:
                return "In";
            case QueryBuilderOperatorEnum.notIn:
                return "Not In";
            default:
                return operator;
        }
    }, [])

    const operatorOptions = useMemo(() => {
        if (!rule.field)
            return [];
        return column?.operators?.map(operator => ({
                value: operator,
                label: getOperatorLabel(operator),
            }))
            ?? [];
    }, [column?.operators, getOperatorLabel, rule.field]);

    const nullable = useMemo(() => {
        if (!rule.field)
            return false;
        return column?.isNullable ?? false;
    }, [column, rule.field]);


    const showOnlyField = useMemo(() => !rule.field || !column?.operators?.length, [rule.field, column?.operators?.length]);

    const showOnlyFieldAndOperator = useMemo(() => !!rule.field && (!rule.operator && !!column?.operators?.length), [column?.operators?.length, rule.field, rule.operator]);

    /**
     * Changes the rule's field in the reducer.
     */
    const changeField = useCallback((e) => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.ChangeField,
            payload: {
                conditionIndex: conditionIndex,
                ruleIndex: ruleIndex,
                field: e.target.value?.field,
                column: e.target.value?.column,
            },
        })
    }, [dispatch, conditionIndex, ruleIndex]);


    /**
     * Changes the rule's operator in the reducer.
     */
    const changeOperator = useCallback((e) => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.ChangeOperator,
            payload: {
                conditionIndex: conditionIndex,
                ruleIndex: ruleIndex,
                operator: e.target.value,
            },
        })
    }, [dispatch, conditionIndex, ruleIndex]);

    /**
     * Removes a rule from the condition.
     */
    const deleteRule = useCallback(() => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.DeleteRule,
            payload: {
                conditionIndex: conditionIndex,
                ruleIndex: ruleIndex,
            },
        })
    }, [dispatch, ruleIndex, conditionIndex]);

    /**
     * Changes the rule's [displayEmptyValuesOnly] in the reducer.
     */
    const changeDisplayEmptyValuesOnly = useCallback((e) => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.ChangeDisplayEmptyValuesOnly,
            payload: {
                conditionIndex: conditionIndex,
                ruleIndex: ruleIndex,
                displayEmptyValuesOnly: e.target.checked,
            },
        })
    }, [dispatch, conditionIndex, ruleIndex])

    /**
     * The sx style mixin for the condition's box.
     */
    const paperMixin = useCallback<ThemeMixin<Theme>>((theme) => {
        return {
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            marginInlineStart: nestedLevel * 4,
            position: 'relative',
            backgroundColor: theme.palette.background.paper,
            padding: 2,
            '&:before': {
                position: 'absolute',
                content: '""',
                top: -16,
                left: -20,
                width: 20,
                height: `calc(50% + 16px)`,
                borderLeft: `1px dashed ${theme.palette.secondary.main}`,
                borderBottom: `1px dashed ${theme.palette.secondary.main}`,
            },
            ...(!last && {
                '&:after': {
                    position: 'absolute',
                    content: '""',
                    top: `calc(50% + 1px)`,
                    left: -20,
                    height: `calc(50% - 1px)`,
                    borderLeft: `1px dashed ${theme.palette.secondary.main}`,
                },
            })
        };
    }, [nestedLevel, last])

    return (
        <>
            <Paper sx={paperMixin}>
                <Grid container spacing={{xs: 2, lg: 1}} sx={{width: '100%'}}>
                    <Grid
                        xs={showOnlyField ? true : 12}
                        sm={showOnlyField ? true : showOnlyFieldAndOperator ? 5 : 6}
                        lg={showOnlyField ? true : 3}
                    >
                        <TextField
                            select
                            fullWidth
                            label={!!rule.field ? 'Field' : undefined}
                            variant={'outlined'}
                            size={'small'}
                            disabled={disabled}
                            SelectProps={{
                                renderValue: (value: any) => value?.column ?? 'Select Field',
                                displayEmpty: true,
                            }}
                            value={rule.field != null ? fieldOptions.find(e => e.value.field === rule.field && e.value.column === rule.column)?.value : ""}
                            onChange={changeField}
                        >
                            {
                                fieldOptions.map((fieldOption) => (
                                    // @ts-ignore
                                    <MenuItem key={fieldOption.label} value={fieldOption.value}>
                                        {fieldOption.label}
                                    </MenuItem>
                                ))
                            }
                        </TextField>
                    </Grid>
                    {
                        !showOnlyField &&
                        <Grid
                            xs={12}
                            sm={showOnlyFieldAndOperator ? 5 : 6}
                            lg={3}
                        >
                            <TextField
                                select
                                fullWidth
                                label={!!rule.operator ? 'Operator' : undefined}
                                variant={'outlined'}
                                size={'small'}
                                disabled={rule.displayEmptyValuesOnly || disabled}
                                SelectProps={{
                                    renderValue: (value) => operatorOptions.find(operatorOption => operatorOption.value === value)?.label
                                        ?? 'Select Operator',
                                    displayEmpty: true,
                                }}
                                value={rule.operator ?? ""}
                                onChange={changeOperator}
                            >
                                {
                                    operatorOptions.map((operatorOption) => (
                                        <MenuItem key={operatorOption.value} value={operatorOption.value}>
                                            {operatorOption.label}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                        </Grid>
                    }
                    {
                        !showOnlyFieldAndOperator &&
                        <>
                            <Grid
                                xs={12}
                                sm={12}
                                md={6}
                                lg={3}
                            >
                                {
                                    !!rule.field && (!!rule.operator || !column?.operators?.length) &&
                                    <QueryBuilderValueInput
                                        column={column!}
                                        rule={rule}
                                        ruleIndex={ruleIndex}
                                        conditionIndex={conditionIndex}
                                        dispatch={dispatch}
                                        disabled={rule.displayEmptyValuesOnly || disabled}
                                    />
                                }
                            </Grid>
                            <Grid xs>
                                {
                                    nullable &&
                                    <FormControlLabel
                                        label="Show Empty Values"
                                        sx={{margin: 0}}
                                        control={
                                            <Checkbox
                                                disabled={disabled}
                                                sx={{paddingBlock: 0.75}}
                                                disableRipple
                                                checked={rule.displayEmptyValuesOnly}
                                                onChange={changeDisplayEmptyValuesOnly}
                                            />
                                        }
                                    />
                                }
                            </Grid>
                        </>
                    }
                    <Grid
                        xs={'auto'}
                    >
                        <Tooltip title={'Remove Rule'} disableInteractive>
                            <span>
                                <IconButton
                                    color={'error'}
                                    onClick={deleteRule}
                                    disabled={disabled}
                                    sx={{backgroundColor: 'background.paper'}}
                                >
                                <DeleteOutlined/>
                            </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
}

export default QueryBuilderRule;

